import { type StepProps, stripUnsafeChars } from './wizard-data'

// Rendered inline, nested under the "No-code Voice Agents" option (see
// StepBuild), inside the card's border. It is an inset grey panel; the framework
// probe below is a full-width divided section instead, because that one is main's
// shipped block and was left untouched.
//
// OPTIONAL: this is qualification data for the marketing zap, never a gate on
// Get started — a free-text requirement would tax the path we most want people
// to take. The label says so explicitly: a question that does not announce it
// is skippable reads as a requirement, and people skip it in silence.
//
// "(Optional)" goes in the LABEL at full label styling rather than in a muted
// italic span beside it — both spellings exist in this repo, and the muted one
// is the wrong pick for a hint whose whole job is to be noticed. It also keeps
// the annotation inside the <label>, so a screen reader announces "optional"
// with the field instead of stranding it in an unassociated sibling.
//
// The question asks what the agent should HANDLE rather than "what agent do you
// want to build" — outcomes are how people think about it, and "Need ... to
// handle" invites a job description rather than a one-word noun.
// No suggestion chips: the placeholder already shows the answer shape.
export const StepAgent = ({ state, update }: StepProps) => (
  // Equally inset grey panel — same treatment as the framework probe (see
  // StepFramework).
  <div className="m-2 flex flex-col gap-3 rounded-md bg-bg3 p-3">
    {/* A real <label> wired to the input, not a <p> beside it: the question IS
        this field's label, and htmlFor is what makes AT announce it and a click
        on the text focus the field. Styled identically (neo's Label treatment,
        as in the framework probe). */}
    <label htmlFor="onboarding-agent-description" className="text-s-500 text-secondary">
      What do you need the agent to handle? (Optional)
    </label>

    {/* One line, h-9 like every other control: the answer is a phrase, and a
        2-row box made this question the tallest thing on the card.
        Angle brackets are stripped at the input so the backend's unsafe-char
        guard can never turn a finished wizard into a 400; no length cap, since
        the backend imposes none and truncating loses the detail we asked for. */}
    {/* NO Enter-to-submit here, deliberately. Enter is inert
        in this field, and it must stay that way:

        1. Finishing is a ONE-WAY action — finishOnboarding posts to core and
           navigates to a product page, REPLACING /onboarding in history. There is
           no back button to a form you submitted by accident, which is why forms
           shaped like this one do not carry keyboard submit shortcuts.
        2. It would be a second trigger for a mutation whose 4xx are swallowed
           without logging (pages/CLAUDE.md) — SER-6080 on this very surface was
           30 duplicate submits out of 625.

        Note this is currently inert only because the wizard renders no <form>.
        Wrapping it in one would hand this input implicit submission for free, so
        anyone doing that has to re-suppress Enter here. */}
    <input
      id="onboarding-agent-description"
      value={state.agentDescription}
      onChange={(e) => update({ agentDescription: stripUnsafeChars(e.target.value) })}
      // No "or" and a trailing ellipsis: the list is a shape to copy, not a menu
      // to pick from, and "or" made the third item read as the last option.
      placeholder="Answer support calls, qualify leads, book appointments..."
      data-testid="onboarding-agent-description"
      // placeholder:text-s-400 drops the placeholder to regular weight: text-s-500
      // carries 500 on the typed value, and a placeholder that inherits it reads
      // as a filled-in answer rather than a hint.
      className="h-9 w-full rounded-lg border border-st2 bg-background px-3 text-foreground text-s-500 outline-none transition-colors placeholder:text-s-400 placeholder:text-secondary focus-visible:ring-1 focus-visible:ring-ring"
    />
  </div>
)
